import { z } from "zod";
import { api } from "../helpers/apiClient";

// ============================================================================
// CLICKHOUSE RBAC - Schemas and Types
// ============================================================================

// Schema for a single role
const RoleSchema = z.object({
  role_name: z.string(),
  is_default: z.boolean(),
  with_admin_option: z.boolean(),
});

// Schema for a single setting
const SettingSchema = z.object({
  profile_name: z.string(),
  setting_name: z.string(),
  value: z.string(),
});

// Schema for role settings
const RoleSettingSchema = z.object({
  role_name: z.string(),
  setting_profile_name: z.string(),
});

// Schema for a single policy
const PolicySchema = z.object({
  policy_name: z.string(),
  select_filter: z.string(),
  is_restrictive: z.boolean(),
  apply_to_list: z.array(z.string()),
});

// Schema for RBAC information response
const RBACInformationSchema = z.object({
  roles: z.array(RoleSchema),
  settings: z.array(SettingSchema),
  role_settings: z.array(RoleSettingSchema),
  policies_by_database: z.record(z.record(z.array(PolicySchema))),
});

export type RBACInformation = z.infer<typeof RBACInformationSchema>;
export type Role = z.infer<typeof RoleSchema>;
export type Setting = z.infer<typeof SettingSchema>;
export type RoleSetting = z.infer<typeof RoleSettingSchema>;
export type Policy = z.infer<typeof PolicySchema>;

// ============================================================================
// USER GROUP RULES - Schemas and Types
// ============================================================================

// Schema for entity and match type combination
const EntityMatchStatsSchema = z.object({
  entity_type: z.string(),
  match_type: z.string(),
  rule_count: z.number(),
  distinct_groupings: z.number(),
});

// Schema for entity type stats
const EntityStatsSchema = z.object({
  entity_type: z.string(),
  total_rules: z.number(),
  user_groups: z.number(),
  distinct_groupings: z.number(),
});

// Schema for match type stats
const MatchTypeStatsSchema = z.object({
  match_type: z.string(),
  rule_count: z.number(),
});

// Schema for totals
const TotalsSchema = z.object({
  total_rules: z.number(),
  total_user_groups: z.number(),
  total_entity_types: z.number(),
  total_match_types: z.number(),
  total_tenants: z.number().optional(),
});

// Schema for per-tenant stats (used in aggregated view)
const PerTenantStatsSchema = z.object({
  tenant: z.string(),
  by_entity_and_match: z.array(EntityMatchStatsSchema),
  by_entity: z.array(EntityStatsSchema),
  by_match_type: z.array(MatchTypeStatsSchema),
  totals: TotalsSchema,
});

// Schema for user group rules statistics response
const UserGroupRulesStatsSchema = z.object({
  tenant: z.string().optional(),
  by_entity_and_match: z.array(EntityMatchStatsSchema),
  by_entity: z.array(EntityStatsSchema),
  by_match_type: z.array(MatchTypeStatsSchema),
  totals: TotalsSchema,
  tenants: z.array(z.string()).optional(),
  per_tenant: z.array(PerTenantStatsSchema).optional(),
});

export type UserGroupRulesStats = z.infer<typeof UserGroupRulesStatsSchema>;
export type EntityMatchStats = z.infer<typeof EntityMatchStatsSchema>;
export type EntityStats = z.infer<typeof EntityStatsSchema>;
export type MatchTypeStats = z.infer<typeof MatchTypeStatsSchema>;
export type Totals = z.infer<typeof TotalsSchema>;

// Schema for tenants list response
const TenantsListSchema = z.object({
  tenants: z.array(z.string()),
});

export type TenantsList = z.infer<typeof TenantsListSchema>;

// ============================================================================
// CLICKHOUSE RBAC APIs
// ============================================================================

/**
 * Fetch ClickHouse RBAC information including roles, settings, and policies
 */
export const getRBACInformation = () =>
  api.get<RBACInformation>("/api/clickhouse-rbac", {}, 0, RBACInformationSchema);

// ============================================================================
// USER GROUP RULES APIs
// ============================================================================

/**
 * Fetch list of tenants that have user_group_rules data
 */
export const getUserGroupRulesTenants = () =>
  api.get<TenantsList>("/api/user-group-rules-stats/tenants", {}, 0, TenantsListSchema);

/**
 * Fetch user group rules statistics including entity types and match types
 * @param tenantFilter - Optional tenant filter ('all' for aggregated, specific tenant name, or undefined for current)
 */
export const getUserGroupRulesStats = (tenantFilter?: string) =>
  api.get<UserGroupRulesStats>(
    "/api/user-group-rules-stats",
    tenantFilter ? { tenant_filter: tenantFilter } : {},
    0,
    UserGroupRulesStatsSchema
  );
